/**
 * CSP Nonce utility for handling dynamic style injection
 * This patches the DOM API to automatically add nonces to dynamically created style elements
 */

// Get the nonce from the meta tag or window object
export function getNonce(): string {
  // Try to get nonce from window object (set by inline script in index.html)
  if (typeof window !== "undefined" && (window as any).__CSP_NONCE__) {
    return (window as any).__CSP_NONCE__;
  }

  // Fallback: try to get from meta tag
  if (typeof document !== "undefined") {
    const meta = document.querySelector('meta[name="csp-nonce"]');
    if (meta) {
      return meta.getAttribute("content") || "";
    }
  }

  return "";
}

// Patch the DOM API to automatically add nonce to style elements
export function patchStyleElementCreation(): void {
  const nonce = getNonce();
  
  if (!nonce || nonce === "__CSP_NONCE__") {
    // Nonce not available or not replaced by nginx
    console.warn("CSP nonce not available, dynamic styles may be blocked");
    return;
  }

  // Store the original createElement
  const originalCreateElement = document.createElement.bind(document);

  // Override createElement to add nonce to style elements
  document.createElement = function <K extends keyof HTMLElementTagNameMap>(
    tagName: K,
    options?: ElementCreationOptions
  ): HTMLElementTagNameMap[K] {
    const element = originalCreateElement(tagName, options);

    // Add nonce to style elements
    if (tagName.toLowerCase() === "style") {
      (element as HTMLStyleElement).nonce = nonce;
    }

    return element;
  };

  // Also patch insertRule for stylesheets that might be created differently
  const originalInsertRule = CSSStyleSheet.prototype.insertRule;
  CSSStyleSheet.prototype.insertRule = function (
    rule: string,
    index?: number
  ): number {
    // Ensure the ownerNode has a nonce if it's a style element
    if (this.ownerNode && (this.ownerNode as HTMLStyleElement).tagName === "STYLE") {
      (this.ownerNode as HTMLStyleElement).nonce = nonce;
    }
    return originalInsertRule.call(this, rule, index);
  };

  console.log("CSP nonce patch applied successfully");
}

// Auto-initialize when this module is imported
if (typeof window !== "undefined") {
  // Run the patch as early as possible
  patchStyleElementCreation();
}